import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { createReplacementOrderService } from "@/services/replacement-order/replacement-order-service.factory";
import { toReplacementOrderRequest } from "@/services/replacement-order/replacement-order.mapper";
import type { ReplacementOrderResult } from "@/services/replacement-order/replacement-order.types";

const replacementOrderService = createReplacementOrderService();

export interface UsePlaceReplacementOrderParams {
  onOrderPlaced: (result: ReplacementOrderResult) => void;
}

export interface PlaceReplacementOrder {
  placeOrder: () => void;
  isPending: boolean;
  isError: boolean;
  reset: () => void;
}

export const usePlaceReplacementOrder = ({
  onOrderPlaced,
}: UsePlaceReplacementOrderParams): PlaceReplacementOrder => {
  const draft = useReplacementDraftStore();
  const inFlight = useRef(false);

  const mutation = useMutation<ReplacementOrderResult, Error>({
    mutationFn: () => replacementOrderService.submit(toReplacementOrderRequest(draft)),
    onSuccess: (result) => onOrderPlaced(result),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // Guards against a second activation within the same submit synchronously:
  // mutation.isPending only turns true on the next render, so a rapid double
  // activation would otherwise issue two submits before the first re-render.
  const placeOrder = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate();
  }, [mutation]);

  return {
    placeOrder,
    isPending: mutation.isPending,
    isError: mutation.isError,
    reset: mutation.reset,
  };
};
